import { styled, Style } from 'inlines'
import React, { memo, useMemo } from 'react'
import { SchemaFormComponent } from './SchemaFormComponent.js'

export type FieldsProps = {
  schemaType?: Record<string, any>
  copyObj?: any
  checksum?: number
  changes?: (v: any, name: string) => void
  exclude?: string[]
  disabled?: string[]
  style?: Style
}

const baseWrapperStyle: Style = {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
}

type Entry =
  | { kind: 'render'; render: Function; schemaProp: any }
  | { kind: 'renderAs'; schemaProp: any; Component: any }
  | { kind: 'default'; schemaProp: any }

const makeSig = (arr?: string[]) => {
  if (!arr?.length) return ''
  const s = arr.slice().sort()
  return s.join('\u0000')
}

export const Fields = memo(
  ({
    schemaType,
    copyObj,
    checksum,
    changes,
    exclude,
    disabled,
    style,
  }: FieldsProps) => {
    if (!schemaType || !copyObj) return null

    const wrapperStyle = useMemo(
      () => (style ? { ...baseWrapperStyle, ...style } : baseWrapperStyle),
      [style],
    )

    const excludeSig = useMemo(() => makeSig(exclude), [exclude])
    const disabledSig = useMemo(() => makeSig(disabled), [disabled])

    const disabledSet = useMemo(() => {
      if (!disabled?.length) return null
      return new Set(disabled)
    }, [disabledSig])

    const { schemaKeys, entries } = useMemo(() => {
      let keys = Object.keys(schemaType)

      const hiddenNested: Record<string, string[]> = {}

      if (exclude?.length) {
        keys = keys.filter((k) => !exclude.includes(k))

        for (const x of exclude) {
          const dot = x.indexOf('.')
          if (dot > 0) {
            const root = x.slice(0, dot)
            const child = x.slice(dot + 1)
            ;(hiddenNested[root] ??= []).push(child)
          }
        }
      }

      const out: Record<string, Entry | null> = {}

      for (const key of keys) {
        const originalProp = schemaType[key]
        if (!originalProp) {
          out[key] = null
          continue
        }
        if (originalProp?.hide) {
          out[key] = null
          continue
        }

        let schemaProp = originalProp

        const hiddenChildren = hiddenNested[key]
        if (hiddenChildren?.length && schemaProp?.props) {
          const newProps = { ...(schemaProp.props || {}) }
          for (const child of hiddenChildren) {
            delete newProps[child]
          }
          schemaProp = { ...schemaProp, props: newProps }
        }

        if (schemaProp?.render) {
          const render = schemaProp.render
          const { render: _r, ...rest } = schemaProp
          out[key] = { kind: 'render', render, schemaProp: rest }
          continue
        }

        if (schemaProp?.renderAs) {
          out[key] = {
            kind: 'renderAs',
            schemaProp,
            Component: schemaProp.renderAs,
          }
          continue
        }

        out[key] = { kind: 'default', schemaProp }
      }

      return { schemaKeys: keys, entries: out }
    }, [schemaType, excludeSig])

    return (
      <styled.div style={wrapperStyle}>
        {schemaKeys.map((item) => {
          const entry = entries[item]
          if (!entry) return null

          const value = copyObj[item] ?? null

          if (entry.kind === 'render') {
            const node = entry.render(
              {
                schemaProp: entry.schemaProp,
                name: item,
                value,
                onChange: changes,
              },
              copyObj,
            )

            return React.isValidElement(node)
              ? React.cloneElement(node, { key: item })
              : null
          }

          if (entry.kind === 'renderAs') {
            const CustomComponent = entry.Component
            return (
              <CustomComponent
                key={item}
                name={item}
                schemaProp={entry.schemaProp}
                value={value}
                copyObj={copyObj}
                onChange={changes}
                disabled={disabled}
              />
            )
          }

          return (
            <SchemaFormComponent
              key={item}
              name={item}
              schemaProp={entry.schemaProp}
              value={value}
              onChange={changes}
              checksum={checksum}
              disabled={disabledSet ? disabledSet.has(item) : false}
            />
          )
        })}
      </styled.div>
    )
  },
  (prev, next) =>
    prev.schemaType === next.schemaType &&
    prev.copyObj === next.copyObj &&
    prev.checksum === next.checksum &&
    prev.changes === next.changes &&
    makeSig(prev.exclude) === makeSig(next.exclude) &&
    makeSig(prev.disabled) === makeSig(next.disabled) &&
    prev.style === next.style,
)
